export default (node) => {
  if (!node) {
    console.warn('VariantInfoUpdater requires a valid DOM node');
    return;
  }

  const productId = node.dataset.productId;

  // Helper function to check if value exists and is not empty

  const hasValue = (value) => {
    return value != null && value !== '' && value !== '0' && value !== 0;
  };

  const handleVariantChange = (event) => {
    const { variant, productId: eventProductId } = event.detail;
    if (eventProductId !== productId) return;
    if (!variant) return;

    // Update all variant-specific information
    updateSku(variant);
    updateDimensions(variant);
    updateMaterials(variant);
    updateStockInfo(variant);
    updateCampaignDate(variant);
    updateDimensionDrawing(variant);
    updateProductLinks(variant);
    updateComparePrice(variant);
    updateProductRating(variant);
    updateCustomizationTab(variant);
  };

  // Update SKU/Item Number displays
  function updateSku(variant) {
    if (!variant?.sku) return;

    const skuElements = document.querySelectorAll('[data-sku]');
    if (!skuElements.length) return;

    skuElements.forEach((element) => {
      element.textContent = variant.sku;
    });
  }

  // Update dimension displays (width, height, depth, diameter, weight)
  function updateDimensions(variant) {
    if (!variant?.dimensions) return;

    const dimensionsElement = document.querySelector('[data-dimensions]');
    if (!dimensionsElement) return;

    const { width, height, depth, diameter, weight } = variant.dimensions;
    const isUs = dimensionsElement.dataset.isUs === 'true';

    // Build size parts string
    let sizeParts = [];

    if (hasValue(diameter)) {
      const value = isUs
        ? (parseFloat(diameter) * 0.393701).toFixed(2)
        : diameter;
      sizeParts.push(`Ø: ${value}`);
    }

    if (hasValue(width)) {
      const value = isUs ? (parseFloat(width) * 0.393701).toFixed(2) : width;
      sizeParts.push(`W: ${value}`);
    }

    if (hasValue(height)) {
      const value = isUs ? (parseFloat(height) * 0.393701).toFixed(2) : height;
      sizeParts.push(`H: ${value}`);
    }

    if (hasValue(depth)) {
      const value = isUs ? (parseFloat(depth) * 0.393701).toFixed(2) : depth;
      sizeParts.push(`D: ${value}`);
    }

    if (sizeParts.length > 0) {
      const unit = isUs ? 'in' : 'cm';
      dimensionsElement.textContent = `${sizeParts.join(' x ')} ${unit}`;
    }

    // Update weight if element exists
    const weightElement = document.querySelector('[data-weight]');
    if (weightElement && hasValue(weight)) {
      const weightValue = isUs
        ? (parseFloat(weight) * 2.20462).toFixed(2)
        : weight;
      const weightUnit = isUs ? 'lb' : 'kg';
      weightElement.textContent = `${weightValue} ${weightUnit}`;
    }

    // Update seat height if exists
    const seatHeightElement = document.querySelector('[data-seat-height]');
    if (seatHeightElement && hasValue(variant.seat_height)) {
      const value = isUs
        ? (parseFloat(variant.seat_height) * 0.393701).toFixed(2)
        : variant.seat_height;
      const unit = isUs ? 'in' : 'cm';
      seatHeightElement.textContent = `${value} ${unit}`;
    }

    // Update backrest height if exists
    const backrestHeightElement = document.querySelector(
      '[data-backrest-height]'
    );
    if (backrestHeightElement && hasValue(variant.backrest_height)) {
      const value = isUs
        ? (parseFloat(variant.backrest_height) * 0.393701).toFixed(2)
        : variant.backrest_height;
      const unit = isUs ? 'in' : 'cm';
      backrestHeightElement.textContent = `${value} ${unit}`;
    }
  }

  // Update material name displays
  function updateMaterials(variant) {
    if (!variant?.material_name) return;

    const materialElements = document.querySelectorAll('[data-material]');
    if (!materialElements.length) return;

    materialElements.forEach((element) => {
      element.textContent = variant.material_name;
    });
  }

  // Update stock info (MTO delivery dates)
  function updateStockInfo(variant) {
    const stockInfoElement = document.querySelector('[data-component="stockInfo"]');
    if (!stockInfoElement) return;

    const mtoDeliveryDateNode = stockInfoElement.querySelector('[data-mto-delivery-date]');
    if (!mtoDeliveryDateNode) return;

    const isUs = mtoDeliveryDateNode.dataset.region === 'US';
    const deliveryTime = isUs ? mtoDeliveryDateNode.dataset.deliveryTimeUs : mtoDeliveryDateNode.dataset.deliveryTime;

    if (variant?.out_of_stock_strategy === 'made_to_order' && hasValue(deliveryTime)) {
      const deliveryString = mtoDeliveryDateNode.dataset.string || '';
      const weekString = mtoDeliveryDateNode.dataset.weekString || '';

      try {
        // Calculate delivery date based on delivery time in days
        const today = new Date();
        const deliveryDate = new Date(
          today.getTime() + parseInt(deliveryTime) * 24 * 60 * 60 * 1000
        );
        const weekNumber = getWeekNumber(deliveryDate);

        mtoDeliveryDateNode.textContent = `${deliveryString} ${weekString} ${weekNumber}`;
        stockInfoElement.classList.remove('hidden');
      } catch (error) {
        stockInfoElement.classList.add('hidden');
      }
    } else if (variant?.out_of_stock_strategy === 'notifications') {
      stockInfoElement.classList.remove('hidden');
    } else {
      stockInfoElement.classList.add('hidden');
    }
  }

  // Update campaign end date countdown
  function updateCampaignDate(variant) {
    const campaignElement = document.querySelector('[data-campaign-date]');
    if (!campaignElement) return;

    const parentElement = campaignElement.parentElement;
    if (!parentElement) return;

    if (!hasValue(variant?.campaign_end_date)) {
      parentElement.classList.add('hidden');
      return;
    }

    try {
      const today = new Date().toISOString().split('T')[0];
      const campaignEnd = variant.campaign_end_date;

      if (campaignEnd === today) {
        const endsToday = campaignElement.dataset.endsToday || 'Campaign ends today';
        campaignElement.textContent = endsToday;
        parentElement.classList.remove('hidden');
      } else if (campaignEnd > today) {
        const todayTime = new Date(today).getTime();
        const endTime = new Date(campaignEnd).getTime();
        const daysLeft = Math.round(
          (endTime - todayTime) / (1000 * 60 * 60 * 24)
        );

        const endsText = campaignElement.dataset.endsText || 'Campaign ends in';
        const daysText = campaignElement.dataset.daysText || 'days';
        campaignElement.textContent = `${endsText} ${daysLeft} ${daysText}`;
        parentElement.classList.remove('hidden');
      } else {
        parentElement.classList.add('hidden');
      }
    } catch (error) {
      parentElement.classList.add('hidden');
    }
  }

  // Update dimension drawing image
  function updateDimensionDrawing(variant) {
    const drawingElement = document.querySelector('[data-dimension-drawing]');
    if (!drawingElement) return;

    const container = drawingElement.closest('div');

    if (hasValue(variant?.dimension_drawing_url)) {
      drawingElement.src = variant.dimension_drawing_url;
      container?.classList.remove('hidden');
    } else {
      container?.classList.add('hidden');
    }
  }

  // Update product links (sample product, spare parts)
  function updateProductLinks(variant) {
    // Update sample product link
    const sampleLinkElement = document.querySelector('[data-sample-link]');
    if (sampleLinkElement) {
      const parentElement = sampleLinkElement.parentElement;
      const sampleObj = variant.sample_product_handle;
      if (sampleObj && hasValue(sampleObj.handle) && hasValue(sampleObj.url)) {
        sampleLinkElement.href = sampleObj.url;
        parentElement?.classList.remove('hidden');
      } else {
        parentElement?.classList.add('hidden');
      }
    }

    // Update spare parts link
    const sparePartsLinkElement = document.querySelector('[data-spare-parts-link]');
    if (sparePartsLinkElement) {
      const parentElement = sparePartsLinkElement.parentElement;
      const spareObj = variant.spare_part_handle;
      if (spareObj && hasValue(spareObj.handle) && hasValue(spareObj.url)) {
        sparePartsLinkElement.href = spareObj.url;
        parentElement?.classList.remove('hidden');
      } else {
        parentElement?.classList.add('hidden');
      }
    }
  }

  // Update compare at price (sale price)
  function updateComparePrice(variant) {
    const comparePriceElement = document.querySelector('[data-compare-price]');
    if (!comparePriceElement) return;

    if (
      hasValue(variant?.compare_at_price) &&
      variant.compare_at_price !== variant?.price
    ) {
      comparePriceElement.textContent = variant.compare_at_price;
      comparePriceElement.classList.remove('hidden');
    } else {
      comparePriceElement.classList.add('hidden');
    }
  }

  // Update product rating widget (Germany only)
  function updateProductRating(variant) {
    const ratingElement = document.querySelector('[data-product-rating]');
    if (!ratingElement || location.hostname !== 'fermliving.com') return;

    if (!hasValue(variant?.sku)) return;

    try {
      // Update the SKU data attribute
      ratingElement.dataset.sku = variant.sku;

      // Reinitialize the rating widget if the productStickerSummary is available
      if (typeof productStickerSummary !== 'undefined') {
        // Clear existing rating
        ratingElement.innerHTML = '';

        // Reinitialize with new SKU
        const summaryBadge = new productStickerSummary();
        summaryBadge.showSummary({
          tsId: 'XF575F4570D00FB0E628016AC1413C06E',
          sku: [variant.sku],
          element: '[data-product-rating]',
          starColor: '#000',
          starSize: '14px',
          fontSize: '12px',
          showRating: 'true',
          scrollToReviews: 'false',
          enablePlaceholder: 'true',
        });
      }
    } catch (error) {
      // Silently fail if rating widget fails to initialize
    }
  }

  // Update customization accordion tab visibility (MTO products only)
  function updateCustomizationTab(variant) {
    const customizationTab = document.querySelector('[data-customization-tab]');
    if (!customizationTab) return;

    if (variant?.out_of_stock_strategy === 'made_to_order') {
      customizationTab.classList.remove('hidden');
    } else {
      customizationTab.classList.add('hidden');
    }
  }

  // Helper function to get ISO week number
  function getWeekNumber(date) {
    const d = new Date(
      Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
    );
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
  }

  // Initialize event listener
  document.addEventListener('variant:change', handleVariantChange);
};
